const state = {
  ctx: null,
  master: null,
  musicBus: null,
  sfxBus: null,
  voiceBus: null,
  sfxCompressor: null,
  music: null,
  unlocked: false,
  listener: { x: 0, y: 1.4, z: 0 },
  settings: {
    volume: 0.6,
    muted: false,
    sfxEnabled: true,
    musicEnabled: true,
    voiceEnabled: true,
  },
};

export function configureAudio(partial) {
  Object.assign(state.settings, partial);
  updateBusGains();
  if (!state.settings.musicEnabled && state.music) stopMusic();
}

export function unlockAudio() {
  if (state.unlocked) return;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  state.ctx = new Ctx();
  if (state.ctx.state === 'suspended') state.ctx.resume().catch(() => {});
  createBuses();
  state.unlocked = true;
  updateBusGains();
  startMusic();
}

export function startMusic() {
  state.music = null;
}

export function stopMusic() {
  state.music = null;
}

export function setListenerPosition(position) {
  state.listener.x = position?.x || 0;
  state.listener.y = (position?.y || 0) + 1.4;
  state.listener.z = position?.z || 0;
  const listener = state.ctx?.listener;
  if (!listener) return;
  setAudioParam(listener.positionX, state.listener.x);
  setAudioParam(listener.positionY, state.listener.y);
  setAudioParam(listener.positionZ, state.listener.z);
  setAudioParam(listener.forwardX, 0);
  setAudioParam(listener.forwardY, 0);
  setAudioParam(listener.forwardZ, -1);
  setAudioParam(listener.upX, 0);
  setAudioParam(listener.upY, 1);
  setAudioParam(listener.upZ, 0);
}

export function playWeaponShot(weapon, position, listenerPosition = null) {
  if (!canPlaySfx()) return;
  if (listenerPosition) setListenerPosition(listenerPosition);
  const profile = weaponProfiles[weapon?.id] || weaponProfiles.smg;
  playClick(profile.click, position, profile.pan);
  tone(profile.body, profile.duration, profile.wave, profile.gain, profile.slide, { position });
  tone(profile.tail, profile.tailDuration, 'sine', profile.tailGain, -profile.tail * 0.35, { position, lowpass: 900 });
}

export function playImpact(type = 'hit', position = null, listenerPosition = null) {
  if (!canPlaySfx()) return;
  if (listenerPosition) setListenerPosition(listenerPosition);
  const gain = type === 'kill' ? 0.11 : type === 'boss' ? 0.14 : 0.07;
  noiseHit(type === 'kill' ? 0.18 : 0.11, gain, { position, bandpass: type === 'kill' ? 420 : 760 });
}

export function tone(freq, duration, type = 'sine', gainValue = 0.08, slide = 0, options = {}) {
  if (!canPlaySfx()) return;
  const ctx = state.ctx;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const output = createSpatialOutput(options.position, options);
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ctx.currentTime);
  if (slide) osc.frequency.exponentialRampToValueAtTime(Math.max(20, freq + slide), ctx.currentTime + duration);
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(gainValue, ctx.currentTime + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
  osc.connect(gain);
  gain.connect(output.input);
  output.output.connect(state.sfxBus);
  osc.start();
  osc.stop(ctx.currentTime + duration + 0.03);
}

export function noiseHit(duration = 0.13, gainValue = 0.08, options = {}) {
  if (!canPlaySfx()) return;
  const ctx = state.ctx;
  const bufferSize = Math.floor(ctx.sampleRate * duration);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  const source = ctx.createBufferSource();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  const output = createSpatialOutput(options.position, options);
  filter.type = 'bandpass';
  filter.frequency.value = options.bandpass || 820;
  filter.Q.value = 0.7;
  gain.gain.value = gainValue;
  source.buffer = buffer;
  source.connect(filter);
  filter.connect(gain);
  gain.connect(output.input);
  output.output.connect(state.sfxBus);
  source.start();
}

export function isAudioUnlocked() { return state.unlocked; }
export function getAudioSettings() { return { ...state.settings }; }
export function getAudioContext() { return state.ctx; }
export function getAudioBuses() {
  return {
    master: state.master,
    musicBus: state.musicBus,
    sfxBus: state.sfxBus,
    voiceBus: state.voiceBus,
  };
}

const weaponProfiles = {
  smg: { click: 1600, body: 540, tail: 220, duration: 0.055, tailDuration: 0.11, wave: 'square', gain: 0.035, tailGain: 0.018, slide: -175, pan: 0.18 },
  shotgun: { click: 900, body: 250, tail: 95, duration: 0.13, tailDuration: 0.24, wave: 'sawtooth', gain: 0.09, tailGain: 0.045, slide: -85, pan: 0.24 },
  rail: { click: 2200, body: 130, tail: 460, duration: 0.16, tailDuration: 0.19, wave: 'square', gain: 0.075, tailGain: 0.035, slide: 235, pan: 0.12 },
  plasma: { click: 1240, body: 380, tail: 120, duration: 0.12, tailDuration: 0.22, wave: 'triangle', gain: 0.07, tailGain: 0.05, slide: 180, pan: 0.16 },
};

function createBuses() {
  const ctx = state.ctx;
  state.master = ctx.createGain();
  state.musicBus = ctx.createGain();
  state.sfxBus = ctx.createGain();
  state.voiceBus = ctx.createGain();
  state.sfxCompressor = ctx.createDynamicsCompressor();
  state.sfxCompressor.threshold.value = -18;
  state.sfxCompressor.knee.value = 18;
  state.sfxCompressor.ratio.value = 4;
  state.sfxCompressor.attack.value = 0.004;
  state.sfxCompressor.release.value = 0.12;
  state.musicBus.connect(state.master);
  state.sfxBus.connect(state.sfxCompressor);
  state.sfxCompressor.connect(state.master);
  state.voiceBus.connect(state.master);
  state.master.connect(ctx.destination);
}

function updateBusGains() {
  const muted = state.settings.muted;
  if (state.master) state.master.gain.value = muted ? 0 : state.settings.volume;
  if (state.musicBus) state.musicBus.gain.value = state.settings.musicEnabled ? 1 : 0;
  if (state.sfxBus) state.sfxBus.gain.value = state.settings.sfxEnabled ? 1 : 0;
  if (state.voiceBus) state.voiceBus.gain.value = state.settings.voiceEnabled ? 1 : 0;
}

function createSpatialOutput(position, options = {}) {
  const ctx = state.ctx;
  const input = ctx.createGain();
  let output = input;
  if (options.lowpass) {
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = options.lowpass;
    input.connect(filter);
    output = filter;
  }
  if (position) {
    const panner = ctx.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = 4;
    panner.maxDistance = 70;
    panner.rolloffFactor = 1.7;
    setAudioParam(panner.positionX, position.x || 0);
    setAudioParam(panner.positionY, (position.y || 0) + 1.2);
    setAudioParam(panner.positionZ, position.z || 0);
    output.connect(panner);
    output = panner;
  }
  return { input, output };
}

function playClick(freq, position, pan = 0) {
  tone(freq + Math.random() * 90, 0.025, 'square', 0.018, 0, { position, pan });
}

function setAudioParam(param, value) {
  if (!param) return;
  if (typeof param.setValueAtTime === 'function') param.setValueAtTime(value, state.ctx.currentTime);
  else param.value = value;
}

function canPlaySfx() {
  return !!state.unlocked && !!state.ctx && !!state.sfxBus && state.settings.sfxEnabled && !state.settings.muted;
}
